import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import OffersService, { type OpportunitySnapshotDocument } from '~/services/OffersService';

import { fileToBase64, validateDocumentFile } from '~/utils/opportunityDocumentFile';

import { offerQueryKeys } from './useGetOffer';

export function useGetOpportunityDocuments(opportunityId: string) {
  return useQuery({
    queryKey: offerQueryKeys.opportunityDocuments(opportunityId),
    queryFn: async () => {
      const response = await OffersService.listOpportunityDocuments(opportunityId);
      return response.result || [];
    },
    enabled: Boolean(opportunityId),
  });
}

export function useUploadOpportunityDocument(opportunityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uploadName, uploadFile }: { uploadName: string; uploadFile: File }) => {
      const validationError = validateDocumentFile(uploadFile);
      if (validationError) {
        throw new Error(validationError);
      }
      const fileBase64 = await fileToBase64(uploadFile);
      return OffersService.uploadOpportunityDocument(opportunityId, {
        name: uploadName,
        filename: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
        fileBase64,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: offerQueryKeys.opportunityDocuments(opportunityId) });
    },
  });
}

export function useDownloadOpportunityDocument(opportunityId: string) {
  return useMutation({
    mutationFn: async (document: OpportunitySnapshotDocument) => {
      await OffersService.downloadOpportunityDocument(opportunityId, document.id, document.filename);
    },
  });
}

export function useDeleteOpportunityDocument(opportunityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (document: OpportunitySnapshotDocument) => {
      await OffersService.deleteOpportunityDocument(opportunityId, document.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: offerQueryKeys.opportunityDocuments(opportunityId) });
    },
  });
}
